import React, { useState, useEffect, useMemo } from 'react';
import { postFetch } from '../../../../fetch/fetch';
import totalLocationData from '../../../../data/locationdata';
import {
    LineChart, Line, BarChart, Bar,
    CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';

// ─── 라이트 테마 (InflowPage와 동일) ───────────────────────────────────────────
const PAGE_BG   = "#F3F4F6";
const CARD_BG   = "#FFFFFF";
const BORDER    = "#E5E7EB";
const TEXT_MAIN = "#111827";
const TEXT_SUB  = "#6B7280";

// ─── 지점별 라인 색상 ──────────────────────────────────────────────────────────
const LOCATION_COLORS: { [key: string]: string } = {
    total:    "#111827",
    gangnam:  "#4F46E5",
    gangnam2: "#06B6D4",
    daechi:   "#10B981",
    daechi2:  "#84CC16",
    daechi3:  "#FACC15",
    songpa:   "#F59E0B",
    bundang:  "#EF4444",
    gimpo:    "#EC4899",
    gangdong: "#8B5CF6",
    hanam:    "#14B8A6",
    sinchon:  "#F97316",
    daegu:    "#6366F1",
    centum:   "#0EA5E9",
    dongtan:  "#A855F7",
};

type PeriodType = "1m" | "3m" | "6m" | "1y" | "custom";
type MetricMode = "rate" | "count";

// ─── 날짜 유틸 ─────────────────────────────────────────────────────────────────
function toStr(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
}

function toDisp(s: string): string {
    return s.replace(/-/g, ".");
}

function daysBetween(start: string, end: string): number {
    return Math.round((new Date(end).getTime() - new Date(start).getTime()) / 86400000);
}

function calcPeriod(p: PeriodType): { start: string; end: string } {
    const today = new Date();
    const end = toStr(today);
    if (p === "1m") {
        const d = new Date(today); d.setMonth(d.getMonth() - 1);
        return { start: toStr(d), end };
    }
    if (p === "3m") {
        const d = new Date(today); d.setMonth(d.getMonth() - 3);
        return { start: toStr(d), end };
    }
    if (p === "6m") {
        const d = new Date(today); d.setMonth(d.getMonth() - 6);
        return { start: toStr(d), end };
    }
    if (p === "1y") {
        const d = new Date(today); d.setFullYear(d.getFullYear() - 1);
        return { start: toStr(d), end };
    }
    return { start: end, end };
}

// 주별 평균 집계 (스냅샷은 sum이 아닌 평균)
function aggregateWeekly(daily: any[]): any[] {
    const weeks: { [key: string]: { sum: any; count: number } } = {};
    for (const d of daily) {
        const dt = new Date(d.date + "T00:00:00");
        const day = dt.getDay();
        const mon = new Date(dt);
        mon.setDate(dt.getDate() - (day === 0 ? 6 : day - 1));
        const key = toStr(mon);
        if (!weeks[key]) weeks[key] = { sum: { date: key }, count: 0 };
        weeks[key].count++;
        for (const k of Object.keys(d)) {
            if (k === "date") continue;
            weeks[key].sum[k] = (weeks[key].sum[k] || 0) + (d[k] || 0);
        }
    }
    return Object.values(weeks)
        .map(({ sum, count }) => {
            const avg: any = { date: sum.date };
            for (const k of Object.keys(sum)) {
                if (k === "date") continue;
                avg[k] = Math.round(((sum[k] / count) * 10)) / 10;
            }
            return avg;
        })
        .sort((a, b) => a.date.localeCompare(b.date));
}

// 월별 평균 집계
function aggregateMonthly(daily: any[]): any[] {
    const months: { [key: string]: { sum: any; count: number } } = {};
    for (const d of daily) {
        const key = d.date.slice(0, 7);
        if (!months[key]) months[key] = { sum: { date: key }, count: 0 };
        months[key].count++;
        for (const k of Object.keys(d)) {
            if (k === "date") continue;
            months[key].sum[k] = (months[key].sum[k] || 0) + (d[k] || 0);
        }
    }
    return Object.values(months)
        .map(({ sum, count }) => {
            const avg: any = { date: sum.date };
            for (const k of Object.keys(sum)) {
                if (k === "date") continue;
                avg[k] = Math.round(((sum[k] / count) * 10)) / 10;
            }
            return avg;
        })
        .sort((a, b) => a.date.localeCompare(b.date));
}

// ─── 컴포넌트 ──────────────────────────────────────────────────────────────────
const EnrollmentTrend: React.FC<any> = (props: any) => {

    const init = calcPeriod("1m");
    const [period,      setPeriod]      = useState<PeriodType>("1m");
    const [startDate,   setStartDate]   = useState<string>(init.start);
    const [endDate,     setEndDate]     = useState<string>(init.end);
    const [metricMode,  setMetricMode]  = useState<MetricMode>("rate");
    const [dailyData,   setDailyData]   = useState<any[]>([]);
    const [inflowData,  setInflowData]  = useState<any[]>([]);
    const [loading,     setLoading]     = useState<boolean>(false);
    const [sortField,   setSortField]   = useState<string>("");
    const [sortDir,     setSortDir]     = useState<"asc"|"desc">("desc");

    // 지점 목록 (academy 필터)
    const academyLocations = useMemo(() => {
        if (!props.user?.academy) return [];
        return totalLocationData.filter(l => l.academy.includes(props.user.academy));
    }, [props.user?.academy]);

    // 기본 선택: 전체 + 주요 4개
    const [selectedLocations, setSelectedLocations] = useState<Set<string>>(() => {
        return new Set(["total", "gangnam", "gangnam2", "daechi", "songpa"]);
    });

    // maxSeat 맵
    const maxSeatMap = useMemo(() => {
        const map: { [key: string]: number } = {};
        academyLocations.forEach(l => { if (l.maxSeat) map[l.english] = l.maxSeat; });
        map.total = academyLocations.reduce((s, l) => s + (l.maxSeat || 0), 0);
        return map;
    }, [academyLocations]);

    useEffect(() => {
        fetchData(startDate, endDate);
        fetchInflow(startDate, endDate);
    }, [startDate, endDate]); // eslint-disable-line

    const fetchData = async (start: string, end: string) => {
        setLoading(true);
        try {
            const result: any = await postFetch("/charts/enrollment/trend", { startDate: start, endDate: end });
            if (result.message === "success") setDailyData(result.daily);
        } catch (e) {
            console.log(e);
        }
        setLoading(false);
    };

    const fetchInflow = async (start: string, end: string) => {
        try {
            const result: any = await postFetch("/charts/enrollment/flow", { startDate: start, endDate: end });
            if (result.message === "success") setInflowData(result.byLocation || []);
        } catch (e) {
            console.log(e);
        }
    };

    const applyPeriod = (p: PeriodType) => {
        setPeriod(p);
        if (p === "custom") return;
        const { start, end } = calcPeriod(p);
        setStartDate(start);
        setEndDate(end);
    };

    const toggleLocation = (loc: string) => {
        setSelectedLocations(prev => {
            const next = new Set(prev);
            next.has(loc) ? next.delete(loc) : next.add(loc);
            return next;
        });
    };

    const toggleSort = (field: string) => {
        if (sortField === field) setSortDir(sortDir === "asc" ? "desc" : "asc");
        else { setSortField(field); setSortDir("desc"); }
    };

    // 지점 필터 적용된 bar 차트 데이터 (total 제외)
    const filteredBarData = useMemo(() => {
        return inflowData
            .filter(r => selectedLocations.has(r.location) && r.location !== "total")
            .map(r => ({ ...r, name: academyLocations.find(l => l.english === r.location)?.korean || r.location }));
    }, [inflowData, selectedLocations, academyLocations]);

    // 마스터 표 데이터 (정렬 포함)
    const tableRows = useMemo(() => {
        const rows = inflowData
            .filter(r => selectedLocations.has(r.location) && r.location !== "total")
            .map(r => ({ ...r, net: (r.newCount || 0) - (r.exitCount || 0) }));
        if (!sortField) return rows;
        return [...rows].sort((a: any, b: any) => {
            const diff = (a[sortField] ?? 0) - (b[sortField] ?? 0);
            return sortDir === "asc" ? diff : -diff;
        });
    }, [inflowData, selectedLocations, sortField, sortDir]);

    // 마스터 표 합계 행
    const tableTotalRow = useMemo(() => {
        if (!tableRows.length) return null;
        const sum = (field: string) => tableRows.reduce((a: number, r: any) => a + (r[field] || 0), 0);
        return {
            newCount: sum("newCount"),
            exitCount: sum("exitCount"),
            net: sum("newCount") - sum("exitCount"),
        };
    }, [tableRows]);

    // count → rate or count 변환 + 기간 집계
    const chartData = useMemo(() => {
        if (!dailyData.length) return [];
        const converted = dailyData.map(d => {
            const entry: any = { date: d.date };
            for (const loc of Object.keys(LOCATION_COLORS)) {
                const count = d[loc] ?? 0;
                if (metricMode === "rate") {
                    const cap = maxSeatMap[loc];
                    entry[loc] = cap && count > 0 ? Math.round((count / cap) * 1000) / 10 : (count > 0 ? count : null);
                } else {
                    entry[loc] = count || null;
                }
            }
            return entry;
        });
        const days = converted.length;
        if (days > 365) return aggregateMonthly(converted);
        if (days > 90)  return aggregateWeekly(converted);
        return converted;
    }, [dailyData, metricMode, maxSeatMap]);

    // 요약 카드용 — 스냅샷 데이터가 실제로 있는 첫/마지막 날
    const lastDay  = [...dailyData].reverse().find(d => (d.total || 0) > 0) || dailyData[dailyData.length - 1];
    const firstDay = dailyData.find(d => (d.total || 0) > 0) || dailyData[0];

    const summaryCards = useMemo(() => {
        if (!lastDay || !firstDay) return null;

        const totalCap = maxSeatMap.total || 0;
        const lastTotal = lastDay.total || 0;
        const firstTotal = firstDay.total || 0;
        const totalRate = totalCap ? Math.round((lastTotal / totalCap) * 1000) / 10 : null;
        const firstRate = totalCap ? Math.round((firstTotal / totalCap) * 1000) / 10 : null;
        const change = totalRate !== null && firstRate !== null ? Math.round((totalRate - firstRate) * 10) / 10 : null;

        let bestLoc = "", bestRate = -Infinity;
        let worstLoc = "", worstRate = Infinity;

        for (const l of academyLocations) {
            const cnt = lastDay[l.english] || 0;
            const cap = maxSeatMap[l.english];
            if (!cap) continue;
            const rate = cnt / cap * 100;
            if (rate > bestRate) { bestRate = rate; bestLoc = l.korean; }
            if (rate < worstRate) { worstRate = rate; worstLoc = l.korean; }
        }

        return { totalRate, change, bestLoc, bestRate: Math.round(bestRate * 10) / 10, worstLoc, worstRate: Math.round(worstRate * 10) / 10 };
    }, [lastDay, firstDay, maxSeatMap, academyLocations]);

    const periodBtnStyle = (p: PeriodType): React.CSSProperties => ({
        padding: "6px 14px", borderRadius: 20,
        border: `1px solid ${period === p ? "#4F46E5" : BORDER}`,
        background: period === p ? "#4F46E5" : CARD_BG,
        color: period === p ? "#fff" : TEXT_SUB,
        cursor: "pointer", fontSize: 13, fontWeight: period === p ? 600 : 400,
    });

    const koreanName = (eng: string) => academyLocations.find(l => l.english === eng)?.korean || eng;

    return (
        <div style={{ padding: "24px 28px", background: PAGE_BG, color: TEXT_MAIN, minHeight: "100%" }}>

            {/* ───────────── 필터 카드 ───────────── */}
            <div style={{ background: CARD_BG, borderRadius: 12, padding: "18px 22px", marginBottom: 20, border: `1px solid ${BORDER}`, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>

                {/* 1줄: 기간 버튼 + date input */}
                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                    <span style={{ fontSize: 13, color: TEXT_SUB, marginRight: 4 }}>기간</span>
                    {(["1m", "3m", "6m", "1y", "custom"] as PeriodType[]).map(p => (
                        <button key={p} style={periodBtnStyle(p)} onClick={() => applyPeriod(p)}>
                            {p === "1m" ? "1개월" : p === "3m" ? "3개월" : p === "6m" ? "6개월" : p === "1y" ? "1년" : "사용자 지정"}
                        </button>
                    ))}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 8 }}>
                        <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPeriod("custom"); }}
                            style={{ background: "#F9FAFB", border: `1px solid ${BORDER}`, color: TEXT_MAIN, borderRadius: 8, padding: "5px 10px", fontSize: 13 }} />
                        <span style={{ color: TEXT_SUB }}>—</span>
                        <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPeriod("custom"); }}
                            style={{ background: "#F9FAFB", border: `1px solid ${BORDER}`, color: TEXT_MAIN, borderRadius: 8, padding: "5px 10px", fontSize: 13 }} />
                    </div>
                    <span style={{ marginLeft: "auto", fontSize: 13, color: TEXT_SUB }}>
                        {toDisp(startDate)} — {toDisp(endDate)} ({daysBetween(startDate, endDate)}일)
                    </span>
                </div>

                {/* 2줄: 지표 토글 */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                    <span style={{ fontSize: 13, color: TEXT_SUB, marginRight: 4 }}>지표</span>
                    {(["rate", "count"] as MetricMode[]).map(m => (
                        <button key={m} onClick={() => setMetricMode(m)}
                            style={{
                                padding: "5px 16px", borderRadius: 8, cursor: "pointer", fontSize: 13,
                                border: `1px solid ${metricMode === m ? "#4F46E5" : BORDER}`,
                                background: metricMode === m ? "#EEF2FF" : CARD_BG,
                                color: metricMode === m ? "#4F46E5" : TEXT_SUB,
                                fontWeight: metricMode === m ? 600 : 400,
                            }}>
                            {m === "rate" ? "충원율 (%)" : "인원수 (명)"}
                        </button>
                    ))}
                </div>

                {/* 3줄: 지점 체크박스 칩 */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, color: TEXT_SUB, marginRight: 4 }}>지점</span>
                    {/* 전체 칩 */}
                    {[{ english: "total", korean: "전사 합계" }, ...academyLocations].map(l => {
                        const on = selectedLocations.has(l.english);
                        const color = LOCATION_COLORS[l.english] || TEXT_SUB;
                        return (
                            <button key={l.english} onClick={() => toggleLocation(l.english)}
                                style={{
                                    display: "flex", alignItems: "center", gap: 6,
                                    padding: "4px 12px", borderRadius: 20, cursor: "pointer", fontSize: 12,
                                    border: `1.5px solid ${on ? color : BORDER}`,
                                    background: on ? color + "18" : "transparent",
                                    color: on ? color : TEXT_SUB,
                                    fontWeight: on ? 600 : 400,
                                }}>
                                <span style={{ width: 8, height: 8, borderRadius: "50%", background: on ? color : BORDER, flexShrink: 0 }} />
                                {l.korean}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ───────────── 요약 카드 4개 ───────────── */}
            {summaryCards && (
                <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
                    {/* 전사 평균 충원율 */}
                    <div style={{ flex: 1, background: CARD_BG, borderRadius: 12, padding: "16px 20px", border: `1px solid ${BORDER}`, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                        <div style={{ fontSize: 12, color: TEXT_SUB, marginBottom: 6 }}>전사 충원율 (최근)</div>
                        <div style={{ fontSize: 32, fontWeight: 700, color: TEXT_MAIN }}>
                            {summaryCards.totalRate !== null ? `${summaryCards.totalRate}%` : "—"}
                        </div>
                        <div style={{ fontSize: 12, color: TEXT_SUB, marginTop: 4 }}>
                            {lastDay?.total ?? 0}명 / {maxSeatMap.total}석
                        </div>
                    </div>

                    {/* 기간 시작 대비 변화 */}
                    <div style={{ flex: 1, background: CARD_BG, borderRadius: 12, padding: "16px 20px", border: `1px solid ${BORDER}`, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                        <div style={{ fontSize: 12, color: TEXT_SUB, marginBottom: 6 }}>기간 시작 대비</div>
                        <div style={{ fontSize: 32, fontWeight: 700, color: summaryCards.change === null ? TEXT_MAIN : summaryCards.change >= 0 ? "#059669" : "#DC2626" }}>
                            {summaryCards.change !== null ? `${summaryCards.change >= 0 ? "+" : ""}${summaryCards.change}%p` : "—"}
                        </div>
                        <div style={{ fontSize: 12, color: TEXT_SUB, marginTop: 4 }}>
                            {toDisp(startDate)} 기준
                        </div>
                    </div>

                    {/* 최고 충원율 */}
                    <div style={{ flex: 1, background: CARD_BG, borderRadius: 12, padding: "16px 20px", border: `1px solid ${BORDER}`, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                        <div style={{ fontSize: 12, color: TEXT_SUB, marginBottom: 6 }}>최고 충원율 지점</div>
                        <div style={{ fontSize: 28, fontWeight: 700, color: "#059669" }}>
                            {summaryCards.bestLoc || "—"}
                        </div>
                        <div style={{ fontSize: 13, color: "#059669", marginTop: 4 }}>
                            {summaryCards.bestRate > 0 ? `${summaryCards.bestRate}%` : ""}
                        </div>
                    </div>

                    {/* 최저 충원율 */}
                    <div style={{ flex: 1, background: CARD_BG, borderRadius: 12, padding: "16px 20px", border: `1px solid ${BORDER}`, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                        <div style={{ fontSize: 12, color: TEXT_SUB, marginBottom: 6 }}>최저 충원율 지점</div>
                        <div style={{ fontSize: 28, fontWeight: 700, color: "#DC2626" }}>
                            {summaryCards.worstLoc || "—"}
                        </div>
                        <div style={{ fontSize: 13, color: "#DC2626", marginTop: 4 }}>
                            {summaryCards.worstRate < Infinity ? `${summaryCards.worstRate}%` : ""}
                        </div>
                    </div>
                </div>
            )}

            {/* ───────────── 메인 라인 차트 ───────────── */}
            <div style={{ background: CARD_BG, borderRadius: 12, padding: "20px 24px", border: `1px solid ${BORDER}`, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_MAIN, marginBottom: 4 }}>충원율 추이</div>
                <div style={{ fontSize: 12, color: TEXT_SUB, marginBottom: 16 }}>
                    {metricMode === "rate" ? "재원생 수 / 정원 × 100" : "실제 재원생 수 (seat 배정 기준)"}
                </div>

                <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={chartData} margin={{ top: 5, right: 16, left: -10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
                        <XAxis
                            dataKey="date"
                            tick={{ fill: TEXT_SUB, fontSize: 10 }}
                            tickFormatter={(v: string) => {
                                if (!v) return "";
                                const p = v.split("-");
                                if (p.length === 3) return `${+p[1]}/${+p[2]}`;
                                if (p.length === 2) return `${+p[1]}월`;
                                return v;
                            }}
                        />
                        <YAxis
                            tick={{ fill: TEXT_SUB, fontSize: 11 }}
                            tickFormatter={v => metricMode === "rate" ? `${v}%` : `${v}명`}
                        />
                        <Tooltip
                            contentStyle={{ background: CARD_BG, border: `1px solid ${BORDER}`, color: TEXT_MAIN }}
                            formatter={(v: any, name: string) => [
                                metricMode === "rate" ? `${v}%` : `${v}명`,
                                name === "total" ? "전사 합계" : koreanName(name),
                            ]}
                        />
                        {Array.from(selectedLocations).map(loc => (
                            <Line
                                key={loc}
                                type="monotone"
                                dataKey={loc}
                                stroke={LOCATION_COLORS[loc] || TEXT_SUB}
                                strokeWidth={loc === "total" ? 3 : 1.5}
                                dot={false}
                                name={loc}
                                connectNulls
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* ───────────── 지점별 신규·퇴원 바 차트 ───────────── */}
            {filteredBarData.length > 0 && (
                <div style={{ background: CARD_BG, borderRadius: 12, padding: "20px 24px", marginTop: 20, border: `1px solid ${BORDER}`, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_MAIN, marginBottom: 4 }}>지점별 신규·퇴원</div>
                    <div style={{ fontSize: 12, color: TEXT_SUB, marginBottom: 14 }}>스냅샷 기준 — 전일 대비 실재원 변동 합산</div>

                    {/* 범례 */}
                    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14, flexWrap: "wrap" }}>
                        {[
                            { color: "#1D9E75", label: "신규" }, { color: "#E24B4A", label: "퇴원" },
                        ].map(({ color, label }) => (
                            <span key={label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: TEXT_SUB }}>
                                <span style={{ width: 10, height: 10, borderRadius: 2, background: color, display: "inline-block" }} />{label}
                            </span>
                        ))}
                    </div>

                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={filteredBarData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
                            <XAxis dataKey="name" tick={{ fill: TEXT_SUB, fontSize: 11 }} />
                            <YAxis tick={{ fill: TEXT_SUB, fontSize: 11 }} />
                            <Tooltip contentStyle={{ background: CARD_BG, border: `1px solid ${BORDER}`, color: TEXT_MAIN }} />
                            <Bar dataKey="newCount"  fill="#1D9E75" name="신규" />
                            <Bar dataKey="exitCount" fill="#E24B4A" name="퇴원" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* ───────────── 마스터 표 ───────────── */}
            {tableRows.length > 0 && (
                <div style={{ background: CARD_BG, borderRadius: 12, marginTop: 20, border: `1px solid ${BORDER}`, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: "#F9FAFB" }}>
                                <th style={{ padding: "10px 14px", textAlign: "left", color: TEXT_SUB, fontWeight: 600, borderBottom: `2px solid ${BORDER}` }}>지점</th>
                                {[
                                    { field: "newCount",  label: "신규" },
                                    { field: "exitCount", label: "퇴원" },
                                    { field: "net",       label: "순증" },
                                ].map(({ field, label }) => (
                                    <th key={field} onClick={() => toggleSort(field)}
                                        style={{ padding: "10px 14px", textAlign: "right", cursor: "pointer", color: TEXT_SUB, fontWeight: 600, borderBottom: `2px solid ${BORDER}`, userSelect: "none" }}>
                                        {label}{sortField === field ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {tableRows.map((row: any, idx: number) => (
                                <tr key={row.location} style={{ background: idx % 2 === 0 ? CARD_BG : "#F9FAFB", borderBottom: `1px solid ${BORDER}` }}>
                                    <td style={{ padding: "10px 14px", color: TEXT_MAIN, fontWeight: 600 }}>
                                        {academyLocations.find(l => l.english === row.location)?.korean || row.location}
                                    </td>
                                    <td style={{ padding: "10px 14px", textAlign: "right", color: TEXT_MAIN }}>{row.newCount}</td>
                                    <td style={{ padding: "10px 14px", textAlign: "right", color: TEXT_MAIN }}>{row.exitCount}</td>
                                    <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700,
                                        color: row.net > 0 ? "#059669" : row.net < 0 ? "#DC2626" : TEXT_SUB }}>
                                        {row.net > 0 ? "+" : ""}{row.net}
                                    </td>
                                </tr>
                            ))}
                            {tableTotalRow && (
                                <tr style={{ background: "#EEF2FF", borderTop: `2px solid ${BORDER}` }}>
                                    <td style={{ padding: "11px 14px", color: TEXT_MAIN, fontWeight: 700 }}>합계</td>
                                    <td style={{ padding: "11px 14px", textAlign: "right", color: TEXT_MAIN, fontWeight: 700 }}>{tableTotalRow.newCount}</td>
                                    <td style={{ padding: "11px 14px", textAlign: "right", color: TEXT_MAIN, fontWeight: 700 }}>{tableTotalRow.exitCount}</td>
                                    <td style={{ padding: "11px 14px", textAlign: "right", fontWeight: 700,
                                        color: tableTotalRow.net > 0 ? "#059669" : tableTotalRow.net < 0 ? "#DC2626" : TEXT_SUB }}>
                                        {tableTotalRow.net > 0 ? "+" : ""}{tableTotalRow.net}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {loading && (
                <div style={{ position: "fixed", top: 20, right: 24, background: "#4F46E5", color: "#fff", borderRadius: 8, padding: "6px 14px", fontSize: 13, zIndex: 9999 }}>
                    로딩 중…
                </div>
            )}
        </div>
    );
};

export default EnrollmentTrend;
